import { Injectable } from '@angular/core';
import { shutdown } from '@intercom/messenger-js-sdk';
import { OAuthService } from 'angular-oauth2-oidc';
import { from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AccountService } from '../services/account.service';
import { StorageService } from '../services/storage.service';
import { authConfig } from './auth.config';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private oauthService: OAuthService,
    private storageService: StorageService,
    private account: AccountService,
  ) {
    this.oauthService.configure(authConfig);
    this.oauthService.setStorage(localStorage);
  }

  login(email: string, token?: string): void {
    this.oauthService.configure(authConfig);
    const encodedEmail = encodeURIComponent(email);
    this.oauthService.initCodeFlow('', { email: encodedEmail, token, login_hint: email });
  }

  resetPassword(token?: string, invite?: boolean): void {
    this.oauthService.configure({ ...authConfig, loginUrl: environment.apiDomain + '/identity/reset-password' });
    this.oauthService.initCodeFlow('', { token, invite });
  }

  register(email: string, firstName: string, lastName: string): void {
    this.oauthService.configure(authConfig);
    const encodedEmail = encodeURIComponent(email);
    const encodedFirstName = encodeURIComponent(firstName);
    const encodedLastName = encodeURIComponent(lastName);
    this.oauthService.initCodeFlow('', {
      signup: 'true',
      email: encodedEmail,
      first_name: encodedFirstName,
      last_name: encodedLastName,
    });
  }

  logout(noRedirect = false): void {
    this.oauthService.logOut(noRedirect);
    this.storageService.clear();
    this.account.clearProfile();
    shutdown();
  }

  tryLoginCodeFlow(): Promise<unknown> {
    return this.oauthService.tryLoginCodeFlow({ preventClearHashAfterLogin: true });
  }

  getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  getUserClaims(): Record<string, any> {
    return this.oauthService.getIdentityClaims() || {};
  }

  getGrantedScopes(): object {
    return this.oauthService.getGrantedScopes();
  }

  hasRefreshToken(): boolean {
    return !!this.oauthService.getRefreshToken();
  }

  refresh(): Observable<boolean> {
    return from(this.oauthService.refreshToken()).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }
}
